import type { FunctionImplementation } from "@otto-ec/global-resources/nexus";
import { dispatchSheetEvent } from "../event/event.dispatch";
import { popStack, popStackAll, pushStack } from "../stack/global.svelte.js";
import { findInstance, getInstance, stackScope } from "../stack/stack.utils.js";
import { SHEET_EVENT_BEFORE_CLOSE, SHEET_EVENT_BEFORE_OPEN } from "../event/event.consts";
import { SHEET_ACTION_PROGRAMMATICALLY } from "../event/event.activation";
import { preflightPop, preflightPush } from "../stack/preflight.svelte.js";
import { applyProps } from "../utils/props";
import { sheetTagName } from "../utils/consts";
import type { SheetV1 } from "../index.js";

const log = /*           */ stackScope.scope("methods");

/**
 *
 */
export const sheetCreate = ((config = {}, options = {}) => {
  /*                                             */
  let sheetElement = config.id ? findInstance(config.id) : null;
  if (!sheetElement) {
    sheetElement = document.createElement(sheetTagName);
  }

  /*                                              */
  const { dataSet = {}, ...conf } = config;
  const { adjacentElement, container = document.body } = options;

  /*                                             */
  applyProps(sheetElement, conf);

  Object.entries(dataSet).forEach(([key, value]) => {
    sheetElement.dataset[key] = value;
  });

  /*                                                         */
  if (adjacentElement) {
    adjacentElement.insertAdjacentElement("afterend", sheetElement);
    return sheetElement;
  }

  /*                                                            */
  container.append(sheetElement as Node);
  return sheetElement;
}) satisfies FunctionImplementation<SheetV1["create"]>;

/**
 *
 */
export const sheetOpenById = ((sheetId, options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { previos, isActive, reason } = preflightPush(sheetId);

  /*                                                                */
  if (isActive) return (log.info("Sheet is already open", sheetId), false);

  /*                                                      */
  if (previos && !findInstance(previos)) {
    log.error("Unexpected stack state, previous instance not found", previos);
    return (popStackAll(action), false);
  }

  const closePayload = { reason, action: action[0], closeType: action[1] };
  if (
    previos &&
    !dispatchSheetEvent(SHEET_EVENT_BEFORE_CLOSE, getInstance(previos), closePayload)
  ) {
    return (log.info("Deactivation refused, by", previos, "could not open", sheetId), false);
  }

  /*                                                                          */
  /*                                                     */
  let instance = findInstance(sheetId);

  const openPayload = { reason, action: action[0], id: sheetId };
  if (!dispatchSheetEvent(SHEET_EVENT_BEFORE_OPEN, instance, openPayload)) {
    return (log.info("Activation refused, by", sheetId, "could not open", sheetId), false);
  }

  /*                                                            */
  instance ??= findInstance(sheetId);
  if (!instance) {
    return (log.info("Could not open", sheetId, "instance not found"), false);
  }

  /*                                                                                        */
  log.info("Activating sheet instance", sheetId);
  return (pushStack(sheetId, action), true);
}) satisfies FunctionImplementation<SheetV1["open"]>;

/**
 *
 */
export const sheetClose = ((options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { isActive, reason, current } = preflightPop();

  /*                                  */
  if (!isActive) return (log.info("No sheet is currently open, cannot go back"), false);

  /*                                                  */
  const payload = { reason, action: action[0], closeType: action[1] };
  if (!dispatchSheetEvent(SHEET_EVENT_BEFORE_CLOSE, getInstance(current), payload)) {
    return (log.info("Deactivation refused, by", current, "could not go back"), false);
  }

  log.info("Deactivating sheet instance", current);
  return (popStackAll(action), true);
}) satisfies FunctionImplementation<SheetV1["close"]>;

/**
 *
 */
export const sheetGoBack = ((options = {}) => {
  const { action = SHEET_ACTION_PROGRAMMATICALLY } = options;
  const { isActive, previos, reason, current } = preflightPop();

  /*                                  */
  if (!isActive) return (log.warn("No sheet is currently open, cannot go back"), false);

  /*                                                               */
  if (!previos) return sheetClose(options);

  if (previos && !findInstance(previos)) {
    log.error("Unexpected stack state, previous instance not found", previos);
    return (popStackAll(action), false);
  }

  if (
    !dispatchSheetEvent(SHEET_EVENT_BEFORE_CLOSE, getInstance(current), {
      reason,
      action: action[0],
      closeType: action[1],
    })
  ) {
    return (log.info("Deactivation refused, by", current, "could not go back"), false);
  }

  /*                                                                          */
  /*                                                     */
  if (
    !dispatchSheetEvent(SHEET_EVENT_BEFORE_OPEN, getInstance(previos), {
      reason,
      action: action[0],
    })
  ) {
    return (log.info("Activation refused, by", previos, "could not open", previos), false);
  }

  log.info("Going back to sheet instance", previos);
  return (popStack(action), true);
}) satisfies FunctionImplementation<SheetV1["back"]>;
